import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { dataMarts, dataPipelines } from '../../../../../../api/coreData'

const WORKFLOW_TYPES = ['fact', 'snapshot', 'timeseries']
const RUN_BY_OPTIONS = ['client', 'account', 'asset', 'tag', 'system-query', 'none']
const PERIOD_OPTIONS = ['year', 'quarter', 'month', 'week', 'day']
const DATA_TYPES = ['varchar', 'int', 'bigint', 'decimal', 'datetime', 'date', 'text', 'boolean']

function createEmptyForm () {
  return {
    timeSeriesId: uuid(),
    name: '',
    description: '',
    enabled: false,
    configuration: {
      workflowType: 'timeseries',
      sourcePipelineId: '',
      runBy: 'client',
      batchSize: 25,
      taskPageSize: 10,
      dataAccessor: 'data',
      processTasksSequentially: false,
      periods: ['quarter', 'month', 'year'],
      minDate: null,
      maxDate: null,
      migrationHash: null,
      exposureFilter: {},
      excludeFromInsert: [],
      behaviors: {},
      defaultParameters: {},
      systemQueryCode: null,
      tableConfiguration: {
        tableName: '',
        tableSchema: 'dataExport',
        columns: [],
        indexes: []
      }
    }
  }
}

function formReducer (state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'SET_CONFIG': {
      return {
        ...state,
        configuration: { ...state.configuration, [action.field]: action.value }
      }
    }
    case 'SET_TABLE_CONFIG': {
      return {
        ...state,
        configuration: {
          ...state.configuration,
          tableConfiguration: {
            ...state.configuration.tableConfiguration,
            [action.field]: action.value
          }
        }
      }
    }
    case 'SET_BEHAVIOR': {
      return {
        ...state,
        configuration: {
          ...state.configuration,
          behaviors: { ...state.configuration.behaviors, [action.field]: action.value }
        }
      }
    }
    case 'LOAD':
      return action.data
    case 'RESET_COLUMNS':
      return {
        ...state,
        configuration: {
          ...state.configuration,
          tableConfiguration: {
            ...state.configuration.tableConfiguration,
            columns: action.columns
          }
        }
      }
    case 'SET_WORKFLOW_TYPE': {
      const next = { ...state, configuration: { ...state.configuration, workflowType: action.value } }
      if (action.value === 'fact') {
        next.configuration.periods = []
        next.configuration.behaviors = {}
        next.configuration.minDate = null
        next.configuration.maxDate = null
      } else if (action.value === 'snapshot') {
        next.configuration.periods = []
        next.configuration.minDate = null
        next.configuration.maxDate = null
        next.configuration.behaviors = { resetStrategy: 'snapshotDate', snapshotDateColumn: 'snapshotDate' }
      } else {
        next.configuration.behaviors = {}
        next.configuration.periods = ['quarter', 'month', 'year']
      }
      return next
    }
    default:
      return state
  }
}

export function useDataMartForm () {
  const { timeSeriesId } = useParams()
  const isNew = !timeSeriesId
  const [form, dispatch] = useReducer(formReducer, null, createEmptyForm)

  const { data: existing, isLoading: isLoadingExisting } = dataMarts.useGetDataMart(timeSeriesId, { enabled: !isNew })
  const { data: pipelineList } = dataPipelines.useListDataPipelines()

  const selectedPipelineId = useMemo(() => {
    if (!form.configuration.sourcePipelineId || !pipelineList?.pipelines) return null
    const match = pipelineList.pipelines.find(p => p.code === form.configuration.sourcePipelineId)
    return match?.pipelineId ?? null
  }, [form.configuration.sourcePipelineId, pipelineList])

  const { data: pipelineDetail } = dataPipelines.useDataPipeline(selectedPipelineId, {
    enabled: !!selectedPipelineId
  })

  useEffect(() => {
    if (!isNew && existing?.timeSeries) {
      const ts = existing.timeSeries
      dispatch({
        type: 'LOAD',
        data: {
          timeSeriesId: ts.timeSeriesId,
          name: ts.name || '',
          description: ts.description || '',
          enabled: !!ts.enabled,
          configuration: {
            workflowType: ts.configuration?.workflowType || 'timeseries',
            sourcePipelineId: ts.configuration?.sourcePipelineId || '',
            runBy: ts.configuration?.runBy || 'client',
            batchSize: ts.configuration?.batchSize ?? 25,
            taskPageSize: ts.configuration?.taskPageSize ?? 10,
            dataAccessor: ts.configuration?.dataAccessor || 'data',
            processTasksSequentially: ts.configuration?.processTasksSequentially ?? false,
            periods: ts.configuration?.periods || [],
            minDate: ts.configuration?.minDate || null,
            maxDate: ts.configuration?.maxDate || null,
            migrationHash: ts.configuration?.migrationHash || null,
            exposureFilter: ts.configuration?.exposureFilter || {},
            excludeFromInsert: ts.configuration?.excludeFromInsert || [],
            behaviors: ts.configuration?.behaviors || {},
            defaultParameters: ts.configuration?.defaultParameters || {},
            systemQueryCode: ts.configuration?.systemQueryCode || null,
            tableConfiguration: ts.configuration?.tableConfiguration || {
              tableName: '',
              tableSchema: 'dataExport',
              columns: [],
              indexes: []
            }
          }
        }
      })
    }
  }, [isNew, existing])

  const createMutation = dataMarts.useCreateDataMart()
  const modifyMutation = dataMarts.useModifyDataMart()
  const reconcileMutation = dataMarts.useReconcileDataMartMetadata()

  const setField = useCallback((field, value) => dispatch({ type: 'SET_FIELD', field, value }), [])
  const setConfig = useCallback((field, value) => dispatch({ type: 'SET_CONFIG', field, value }), [])
  const setTableConfig = useCallback((field, value) => dispatch({ type: 'SET_TABLE_CONFIG', field, value }), [])
  const setBehavior = useCallback((field, value) => dispatch({ type: 'SET_BEHAVIOR', field, value }), [])
  const setWorkflowType = useCallback((value) => dispatch({ type: 'SET_WORKFLOW_TYPE', value }), [])

  const deriveColumnsFromPipeline = useCallback(() => {
    const pipeline = pipelineDetail?.pipeline
    if (!pipeline?.analysis?.fields) return
    const columns = pipeline.analysis.fields.map((f, idx) => ({
      _id: Math.random().toString(36).slice(2),
      columnName: f.accessor,
      dataType: f.type === 'number' ? 'decimal' : 'varchar',
      maxLength: f.type === 'string' ? 256 : null,
      isNullable: true,
      isPrimaryKey: false,
      ordinalPosition: idx + 1
    }))
    dispatch({ type: 'RESET_COLUMNS', columns })
  }, [pipelineDetail])

  const resetMigrationHash = useCallback(() => {
    dispatch({ type: 'SET_CONFIG', field: 'migrationHash', value: null })
  }, [])

  const save = useCallback(async () => {
    const payload = {
      timeSeriesId: form.timeSeriesId,
      name: form.name,
      description: form.description || null,
      configuration: form.configuration,
      enabled: form.enabled
    }

    if (isNew) {
      return createMutation.mutateAsync(payload)
    }
    return modifyMutation.mutateAsync(payload)
  }, [form, isNew, createMutation, modifyMutation])

  const reconcile = useCallback(async (dryRun = true) => {
    return reconcileMutation.mutateAsync({
      timeSeriesId: form.timeSeriesId,
      dryRun
    })
  }, [form.timeSeriesId, reconcileMutation])

  return {
    form,
    isNew,
    isLoading: !isNew && isLoadingExisting,
    isSaving: createMutation.isLoading || modifyMutation.isLoading,
    isReconciling: reconcileMutation.isLoading,
    reconcileResult: reconcileMutation.data,
    pipelineList: pipelineList?.pipelines || [],
    pipelineDetail: pipelineDetail?.pipeline || null,
    setField,
    setConfig,
    setTableConfig,
    setBehavior,
    setWorkflowType,
    deriveColumnsFromPipeline,
    resetMigrationHash,
    save,
    reconcile,
    WORKFLOW_TYPES,
    RUN_BY_OPTIONS,
    PERIOD_OPTIONS,
    DATA_TYPES
  }
}
